import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Plus, FileText, Calendar, CheckCircle2, Clock, AlertCircle, Mail, RefreshCw, Trash2, Loader2, Paperclip, Download, Filter, ImagePlus, Sparkles, FolderOpen, FileText as FileIcon, X, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import DatosStartup from './DatosStartup';
import CrearReporte from './CrearReporte';
import EnviarEmail from './EnviarEmail';
import InputWithDictation from './InputWithDictation';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { 
  processEmailsAPI, 
  getReporte, 
  getReportes, 
  getStartups, 
  createReporte, 
  deleteReporte,
  updateReporteContenidoGenerico,
  rellenarDesdeDrive,
  getDriveStatus,
  listDriveFiles,
  searchDriveFiles,
  getAttachments,
  downloadAttachment
} from '../services/apiService';

const PreparacionReportes = () => {
  const [reportes, setReportes] = useState([]);
  const [startups, setStartups] = useState([]);

  const [mostrarCrearReporte, setMostrarCrearReporte] = useState(false);
  const [reporteSeleccionado, setReporteSeleccionado] = useState(null);

  // Cargar reportes y startups desde el backend
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const [reportesData, startupsData] = await Promise.all([
          getReportes(),
          getStartups()
        ]);
        setReportes(reportesData || []);
        setStartups(startupsData || []);
      } catch (error) {
        console.error('Error cargando datos:', error);
      }
    };
    cargarDatos();
  }, []);

  const handleCrearReporte = () => {
    setMostrarCrearReporte(true);
  };

  const handleGuardarReporte = async (nuevoReporte) => {
    try {
      const createdReporte = await createReporte(nuevoReporte);
      setReportes([...reportes, createdReporte]);
      setMostrarCrearReporte(false);
    } catch (error) {
      console.error('Error creando reporte:', error);
      alert('Error al crear el reporte');
    }
  };

  const handleEliminarReporte = async (reporteId, e) => {
    e.stopPropagation();
    if (window.confirm('¿Estás seguro de que quieres eliminar este reporte? Esta acción no se puede deshacer.')) {
      try {
        await deleteReporte(reporteId);
        setReportes(reportes.filter(r => r.id !== reporteId));
      } catch (error) {
        console.error('Error eliminando reporte:', error);
        alert('Error al eliminar el reporte');
      }
    }
  };

  const handleAbrirReporte = (reporte) => {
    setReporteSeleccionado(reporte);
  };

  const handleVolver = () => {
    setReporteSeleccionado(null);
  };

  if (mostrarCrearReporte) {
    return (
      <CrearReporte
        startups={startups}
        onCrear={handleGuardarReporte}
        onCancelar={() => setMostrarCrearReporte(false)}
      />
    );
  }

  if (reporteSeleccionado) {
    return (
      <DetalleReporte 
        reporte={reporteSeleccionado}
        startups={startups}
        onVolver={handleVolver}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Preparación de Reportes</h1>
          <p className="text-muted-foreground mt-2">
            Gestiona y crea reportes trimestrales de startups participadas
          </p>
        </div>
        <Button onClick={handleCrearReporte}>
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Reporte
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {reportes.map((reporte) => (
          <Card 
            key={reporte.id}
            className="border-border/50 shadow-sm hover:shadow-md transition-shadow relative group"
          >
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle 
                  className="text-lg cursor-pointer flex-1"
                  onClick={() => handleAbrirReporte(reporte)}
                >
                  {reporte.nombre}
                </CardTitle>
                <div className="flex items-center gap-2">
                  {reporte.estado === 'completado' ? (
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                  ) : (
                    <Clock className="h-5 w-5 text-yellow-600" />
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive"
                    onClick={(e) => handleEliminarReporte(reporte.id, e)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <CardDescription>
                <Calendar className="h-4 w-4 inline mr-1" />
                Creado el {new Date(reporte.fechaCreacion).toLocaleDateString('es-ES')}
              </CardDescription>
            </CardHeader>
            <CardContent 
              className="cursor-pointer"
              onClick={() => handleAbrirReporte(reporte)}
            >
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Progreso:</span>
                  <span className="font-medium">
                    {reporte.startupsCompletas || 0} / {reporte.startupsTotales || 0}
                  </span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ 
                      width: `${reporte.startupsTotales > 0 
                        ? ((reporte.startupsCompletas || 0) / reporte.startupsTotales) * 100 
                        : 0}%` 
                    }}
                  />
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <FileText className="h-3 w-3" />
                  <span>{reporte.startupsTotales || 0} startups participadas</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

const DetalleReporte = ({ reporte, startups, onVolver }) => {
  const [reporteActualizado, setReporteActualizado] = useState(reporte);
  const [startupSeleccionada, setStartupSeleccionada] = useState(null);
  const [mostrarEnviarEmail, setMostrarEnviarEmail] = useState(null);
  const [procesandoEmails, setProcesandoEmails] = useState(false);
  const [mensajeProcesamiento, setMensajeProcesamiento] = useState(null);
  const [attachmentsByStartup, setAttachmentsByStartup] = useState({});
  const [filtroEstado, setFiltroEstado] = useState('todos'); // 'todos', 'pendiente', 'incompleto', 'completado'
  const [tabActiva, setTabActiva] = useState('startups'); // 'generico' | 'startups'
  const [contenidoGenerico, setContenidoGenerico] = useState({});
  const [guardandoGenerico, setGuardandoGenerico] = useState(false);
  const [startupsReporte, setStartupsReporte] = useState(() => {
    // Crear estado inicial basado en startupsIds del reporte
    return (reporte.startupsIds || []).map(id => {
      const startup = startups.find(s => s.id === id);
      if (!startup) return null;
      
      return {
        ...startup,
        estado: 'pendiente',
        ultimaActualizacion: null,
        datos: null
      };
    }).filter(Boolean);
  });

  // Cargar reporte desde el backend si tiene ID
  useEffect(() => {
    const cargarReporteDesdeBackend = async () => {
      if (reporte && reporte.id) {
        try {
          const reporteBackend = await getReporte(reporte.id);
          if (reporteBackend && reporteBackend.id) {
            setReporteActualizado(reporteBackend);
            
            // Actualizar startupsReporte con las startups del reporte
            if (reporteBackend.startupsIds && reporteBackend.startupsIds.length > 0) {
              const startupsDelReporte = reporteBackend.startupsIds.map(startupId => {
                const startup = startups.find(s => s.id === startupId);
                if (!startup) return null;
                
                // Obtener datos del backend si existen
                const datosBackend = reporteBackend.datosStartups?.[startupId];
                if (!datosBackend) {
                  return {
                    ...startup,
                    datos: null,
                    estado: 'pendiente',
                    ultimaActualizacion: null
                  };
                }
                
                // Verificar si todos los campos requeridos están completos
                const kpis = datosBackend.kpis || {};
                const milestones = datosBackend.milestones || [];
                
                const tieneIngresos = kpis.ingresos !== null && kpis.ingresos !== undefined && kpis.ingresos !== '';
                const tieneGastos = kpis.gastos !== null && kpis.gastos !== undefined && kpis.gastos !== '';
                const tieneEmpleados = kpis.empleados !== null && kpis.empleados !== undefined && kpis.empleados !== '';
                const tieneRunway = kpis.runway !== null && kpis.runway !== undefined && kpis.runway !== '';
                
                // Si rondaAbierta es true, verificar que tenga valoracion y progresoRonda
                let tieneRondaCompleta = true;
                if (kpis.rondaAbierta === true || kpis.rondaAbierta === 'true' || kpis.rondaAbierta === 'Sí') {
                  const tieneValoracion = kpis.valoracion !== null && kpis.valoracion !== undefined && kpis.valoracion !== '';
                  const tieneProgreso = kpis.progresoRonda !== null && kpis.progresoRonda !== undefined && kpis.progresoRonda !== '';
                  tieneRondaCompleta = tieneValoracion && tieneProgreso;
                }
                
                const tieneMilestones = Array.isArray(milestones) && milestones.length > 0;
                
                const estaCompleta = tieneIngresos && tieneGastos && tieneEmpleados && tieneRunway && tieneRondaCompleta && tieneMilestones;
                const tieneAlgunDato = tieneIngresos || tieneGastos || tieneEmpleados || tieneRunway || tieneMilestones;
                
                // Usar el estado guardado en la BD si existe, sino calcularlo automáticamente
                let estadoFinal;
                if (datosBackend.estado && ['pendiente', 'incompleto', 'completado'].includes(datosBackend.estado)) {
                  // Usar el estado guardado manualmente por el usuario
                  estadoFinal = datosBackend.estado;
                } else {
                  // Calcular automáticamente solo si no hay estado guardado
                  // Usar 'completado' (con 'd') para ser consistente con el estado manual
                  estadoFinal = estaCompleta ? 'completado' : (tieneAlgunDato ? 'incompleto' : 'pendiente');
                }
                
                return {
                  ...startup,
                  datos: datosBackend,
                  estado: estadoFinal,
                  ultimaActualizacion: datosBackend.ultimaActualizacion || null
                };
              }).filter(Boolean);
              
              setStartupsReporte(startupsDelReporte);
            }
          }
        } catch (error) {
          console.warn('No se pudo cargar reporte desde backend:', error);
        }
      }
    };
    cargarReporteDesdeBackend();
  }, [reporte, startups]);

  // Sincronizar contenido genérico cuando el reporte se carga desde el backend (si no viene, dejar {} para que el form muestre contenido por defecto)
  useEffect(() => {
    const cg = reporteActualizado?.contenidoGenerico;
    setContenidoGenerico(cg && typeof cg === 'object' ? cg : {});
  }, [reporteActualizado?.id, reporteActualizado?.contenidoGenerico]);

  // Cargar datos actualizados desde el backend
  const cargarDatosActualizados = async () => {
    // Usar reporteActualizado que tiene el ID del backend
    const reporteId = reporteActualizado?.id || reporte?.id;
    
    // Verificar que el reporte tenga un ID válido
    if (!reporteId) {
      console.warn('⚠️ Reporte sin ID, no se pueden cargar datos del backend');
      return;
    }

    try {
      console.log('🔄 Recargando datos del reporte desde el backend...');
      
      // Recargar el reporte completo desde el backend para obtener datos actualizados
      const reporteBackend = await getReporte(reporteId);
      if (!reporteBackend) {
        console.warn('⚠️ No se encontró el reporte en el backend');
        return;
      }
      
      console.log('✅ Reporte cargado:', reporteBackend);
      setReporteActualizado(reporteBackend);
      
      // Reconstruir completamente la lista de startups desde el backend
      if (reporteBackend.startupsIds && reporteBackend.startupsIds.length > 0) {
        const startupsDelReporte = reporteBackend.startupsIds.map(startupId => {
          const startup = startups.find(s => s.id === startupId);
          if (!startup) {
            console.warn(`⚠️ Startup ${startupId} no encontrada en la lista de startups`);
            return null;
          }
          
          // Obtener datos del backend si existen
          const datosBackend = reporteBackend.datosStartups?.[startupId];
          if (!datosBackend) {
            return {
              ...startup,
              datos: null,
              estado: 'pendiente', // Estado por defecto si no hay datos
              ultimaActualizacion: null
            };
          }
          
          // Verificar si todos los campos requeridos están completos
          const kpis = datosBackend.kpis || {};
          const milestones = datosBackend.milestones || [];
          
          const tieneIngresos = kpis.ingresos !== null && kpis.ingresos !== undefined && kpis.ingresos !== '';
          const tieneGastos = kpis.gastos !== null && kpis.gastos !== undefined && kpis.gastos !== '';
          const tieneEmpleados = kpis.empleados !== null && kpis.empleados !== undefined && kpis.empleados !== '';
          const tieneRunway = kpis.runway !== null && kpis.runway !== undefined && kpis.runway !== '';
          
          // Si rondaAbierta es true, verificar que tenga valoracion y progresoRonda
          let tieneRondaCompleta = true;
          if (kpis.rondaAbierta === true || kpis.rondaAbierta === 'true' || kpis.rondaAbierta === 'Sí') {
            const tieneValoracion = kpis.valoracion !== null && kpis.valoracion !== undefined && kpis.valoracion !== '';
            const tieneProgreso = kpis.progresoRonda !== null && kpis.progresoRonda !== undefined && kpis.progresoRonda !== '';
            tieneRondaCompleta = tieneValoracion && tieneProgreso;
          }
          
          const tieneMilestones = Array.isArray(milestones) && milestones.length > 0;
          
          const estaCompleta = tieneIngresos && tieneGastos && tieneEmpleados && tieneRunway && tieneRondaCompleta && tieneMilestones;
          const tieneAlgunDato = tieneIngresos || tieneGastos || tieneEmpleados || tieneRunway || tieneMilestones;
          
          // Usar el estado guardado en la BD si existe, sino calcularlo automáticamente
          let estadoFinal;
          if (datosBackend.estado && ['pendiente', 'incompleto', 'completado'].includes(datosBackend.estado)) {
            // Usar el estado guardado manualmente por el usuario
            estadoFinal = datosBackend.estado;
          } else {
            // Calcular automáticamente solo si no hay estado guardado
            // Usar 'completado' (con 'd') para ser consistente con el estado manual
            estadoFinal = estaCompleta ? 'completado' : (tieneAlgunDato ? 'incompleto' : 'pendiente');
          }
          
          return {
            ...startup,
            datos: datosBackend,
            estado: estadoFinal,
            ultimaActualizacion: datosBackend.ultimaActualizacion || null
          };
        }).filter(Boolean);
        
        console.log('✅ Startups reconstruidas:', startupsDelReporte.length);
        setStartupsReporte(startupsDelReporte);
        
        // Cargar archivos adjuntos para cada startup
        console.log('📎 Cargando archivos adjuntos...');
        const attachmentsPromises = reporteBackend.startupsIds.map(async (startupId) => {
          try {
            const attachments = await getAttachments(reporteId, startupId);
            console.log(`✅ Archivos adjuntos para startup ${startupId}:`, attachments.length);
            return { startupId, attachments };
          } catch (error) {
            console.error(`❌ Error cargando archivos adjuntos para startup ${startupId}:`, error);
            return { startupId, attachments: [] };
          }
        });
        
        const attachmentsResults = await Promise.all(attachmentsPromises);
        const attachmentsMap = {};
        attachmentsResults.forEach(({ startupId, attachments }) => {
          attachmentsMap[startupId] = attachments;
        });
        console.log('✅ Archivos adjuntos cargados:', attachmentsMap);
        setAttachmentsByStartup(attachmentsMap);
      } else {
        console.warn('⚠️ El reporte no tiene startups asociadas');
        setStartupsReporte([]);
        setAttachmentsByStartup({});
      }
    } catch (error) {
      console.error('❌ Error cargando datos actualizados:', error);
      console.error('❌ Stack:', error.stack);
    }
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      'completado': { 
        icon: CheckCircle2, 
        color: 'text-green-600', 
        bg: 'bg-green-50 dark:bg-green-950',
        label: 'Completado'
      },
      // Compatibilidad con 'completo' (sin 'd') por si acaso
      'completo': { 
        icon: CheckCircle2, 
        color: 'text-green-600', 
        bg: 'bg-green-50 dark:bg-green-950',
        label: 'Completado'
      },
      'pendiente': { 
        icon: Clock, 
        color: 'text-yellow-600', 
        bg: 'bg-yellow-50 dark:bg-yellow-950',
        label: 'Pendiente'
      },
      'incompleto': { 
        icon: AlertCircle, 
        color: 'text-orange-600', 
        bg: 'bg-orange-50 dark:bg-orange-950',
        label: 'Incompleto'
      }
    };
    return estados[estado] || estados.pendiente;
  };

  const handleEnviarSolicitud = (startup) => {
    setMostrarEnviarEmail({ startup, tipo: 'solicitud' });
  };

  const handleEnviarRecordatorio = (startup) => {
    setMostrarEnviarEmail({ startup, tipo: 'recordatorio' });
  };

  const handleEnviarRecordatorioFinal = (startup) => {
    setMostrarEnviarEmail({ startup, tipo: 'recordatorioFinal' });
  };

  const handleEmailEnviado = (startupId, tipo) => {
    // Actualizar estado de la startup
    setStartupsReporte(prev => prev.map(s => {
      if (s.id === startupId) {
        return {
          ...s,
          ultimoEmailEnviado: new Date().toISOString(),
          tipoUltimoEmail: tipo
        };
      }
      return s;
    }));
    setMostrarEnviarEmail(null);
  };

  const handleVerDatos = (startup) => {
    setStartupSeleccionada(startup);
  };

  const handleSincronizarEmail = async () => {
    setProcesandoEmails(true);
    setMensajeProcesamiento(null);
    
    try {
      // Obtener el ID del reporte actual
      const reporteId = reporteActualizado?.id || reporte?.id;
      console.log('🔄 Iniciando procesamiento de emails...', reporteId ? `(reporte ${reporteId})` : '');
      const result = await processEmailsAPI(reporteId);
      console.log('✅ Resultado del procesamiento:', result);
      
      if (result && result.success) {
        const detalles = result.processed > 0 
          ? `Se procesaron ${result.processed} email(s) de ${result.total || result.processed} encontrado(s)`
          : result.message || 'No se encontraron emails nuevos';
        
        setMensajeProcesamiento({
          tipo: 'success',
          mensaje: result.message || 'Emails procesados correctamente',
          detalles: detalles
        });
        
        // Cargar datos actualizados después de procesar (aumentar tiempo para asegurar que se guardaron)
        setTimeout(() => {
          console.log('🔄 Recargando datos después de procesar emails...');
          cargarDatosActualizados();
        }, 2000);
      } else {
        console.error('❌ Error en resultado:', result);
        setMensajeProcesamiento({
          tipo: 'error',
          mensaje: result?.error || result?.message || 'Error al procesar emails'
        });
      }
    } catch (error) {
      console.error('❌ Error sincronizando emails:', error);
      console.error('❌ Stack:', error.stack);
      setMensajeProcesamiento({
        tipo: 'error',
        mensaje: error.message || 'Error al conectar con el servidor. Verifica que tengas Gmail conectado en Configuración.'
      });
    } finally {
      setProcesandoEmails(false);
      // Limpiar mensaje después de 5 segundos
      setTimeout(() => {
        setMensajeProcesamiento(null);
      }, 5000);
    }
  };

  if (mostrarEnviarEmail) {
    return (
      <EnviarEmail
        startup={mostrarEnviarEmail.startup}
        reporte={reporteActualizado || reporte}
        tipo={mostrarEnviarEmail.tipo}
        onEnviar={() => handleEmailEnviado(mostrarEnviarEmail.startup.id, mostrarEnviarEmail.tipo)}
        onCancelar={() => setMostrarEnviarEmail(null)}
      />
    );
  }

  if (startupSeleccionada) {
    return (
      <DatosStartup 
        startup={startupSeleccionada}
        reporte={reporteActualizado || reporte}
        onClose={() => {
          setStartupSeleccionada(null);
          // Recargar datos después de cerrar
          cargarDatosActualizados();
        }}
        onUpdate={(updatedStartup) => {
          // Actualizar el estado de la startup en el array local
          setStartupsReporte(prev => prev.map(s => 
            s.id === updatedStartup.id ? { ...s, estado: updatedStartup.estado, ...updatedStartup } : s
          ));
          // Actualizar también startupSeleccionada si es la misma
          if (startupSeleccionada && startupSeleccionada.id === updatedStartup.id) {
            setStartupSeleccionada({ ...startupSeleccionada, estado: updatedStartup.estado, ...updatedStartup });
          }
          // Recargar datos del backend para asegurar sincronización
          cargarDatosActualizados();
        }}
        onSave={() => {
          // Recargar datos después de guardar
          cargarDatosActualizados();
        }}
      />
    );
  }

  const reporteId = reporteActualizado?.id || reporte?.id;
  const abrirInformeWeb = () => {
    if (reporteId) {
      const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
      window.open(`${window.location.origin}${base}/reporte/${reporteId}`, '_blank');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={onVolver} className="mb-2">
            ← Volver
          </Button>
          <h1 className="text-3xl font-semibold tracking-tight">{reporteActualizado?.nombre || reporte.nombre}</h1>
          <p className="text-muted-foreground mt-2">
            Gestiona la recopilación de datos de las startups participadas
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <Button
            variant="default"
            onClick={reporteId ? abrirInformeWeb : undefined}
            disabled={!reporteId}
          >
            <FileText className="h-4 w-4 mr-2" />
            Ver informe web
          </Button>
          <Button 
            variant="outline" 
            onClick={handleSincronizarEmail}
            disabled={procesandoEmails}
          >
            {procesandoEmails ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Procesando...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Procesar Emails
              </>
            )}
          </Button>
          {mensajeProcesamiento && (
            <div className={cn(
              "text-xs px-3 py-1 rounded-md",
              mensajeProcesamiento.tipo === 'success' 
                ? "bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300"
                : "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300"
            )}>
              {mensajeProcesamiento.mensaje}
              {mensajeProcesamiento.detalles && (
                <div className="mt-1">{mensajeProcesamiento.detalles}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Pestañas: Información genérica | Datos de startups */}
      <div className="flex border-b border-border/50">
        <button
          type="button"
          onClick={() => setTabActiva('generico')}
          className={cn(
            "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
            tabActiva === 'generico'
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          Información genérica
        </button>
        <button
          type="button"
          onClick={() => setTabActiva('startups')}
          className={cn(
            "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
            tabActiva === 'startups'
              ? "border-primary text-primary"
              : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          Datos de startups
        </button>
      </div>

      {tabActiva === 'generico' ? (
        <FormularioContenidoGenerico
          key={`generico-${reporteActualizado?.id ?? reporte?.id ?? 0}`}
          reporteId={reporteActualizado?.id || reporte?.id}
          contenido={contenidoGenerico}
          onChange={setContenidoGenerico}
          onSave={async (nuevoContenido) => {
            const id = reporteActualizado?.id || reporte?.id;
            if (!id) return;
            setGuardandoGenerico(true);
            try {
              await updateReporteContenidoGenerico(id, nuevoContenido);
              const reporteBackend = await getReporte(id);
              setReporteActualizado(reporteBackend);
            } catch (e) {
              console.error(e);
              alert('Error al guardar.');
            } finally {
              setGuardandoGenerico(false);
            }
          }}
          guardando={guardandoGenerico}
        />
      ) : (
        <>
      {/* Filtro por estado */}
      <Card className="border-border/50 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">Filtrar por estado:</span>
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  variant={filtroEstado === 'todos' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltroEstado('todos')}
                  className="h-8"
                >
                  Todos ({startupsReporte.length})
                </Button>
                <Button
                  variant={filtroEstado === 'pendiente' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltroEstado('pendiente')}
                  className="h-8"
                >
                  <Clock className="h-3 w-3 mr-1" />
                  Pendiente ({startupsReporte.filter(s => s.estado === 'pendiente').length})
                </Button>
                <Button
                  variant={filtroEstado === 'incompleto' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltroEstado('incompleto')}
                  className="h-8"
                >
                  <AlertCircle className="h-3 w-3 mr-1" />
                  Incompleto ({startupsReporte.filter(s => s.estado === 'incompleto').length})
                </Button>
                <Button
                  variant={filtroEstado === 'completado' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFiltroEstado('completado')}
                  className="h-8"
                >
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Completado ({startupsReporte.filter(s => s.estado === 'completado' || s.estado === 'completo').length})
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4">
        {(() => {
          // Filtrar startups según el estado seleccionado de forma segura
          let startupsFiltradas = [];
          
          if (filtroEstado === 'todos') {
            startupsFiltradas = startupsReporte;
          } else if (filtroEstado === 'completado') {
            // Incluir tanto 'completado' como 'completo' para compatibilidad
            startupsFiltradas = startupsReporte.filter(s => 
              s && s.estado && (s.estado === 'completado' || s.estado === 'completo')
            );
          } else {
            // Para 'pendiente' e 'incompleto', filtrar exactamente
            startupsFiltradas = startupsReporte.filter(s => 
              s && s.estado && s.estado === filtroEstado
            );
          }

          if (startupsFiltradas.length === 0) {
            return (
              <Card className="border-border/50 shadow-sm">
                <CardContent className="p-6 text-center text-muted-foreground">
                  <p>
                    {startupsReporte.length === 0 
                      ? 'No hay startups asociadas a este reporte.'
                      : `No hay startups con estado "${filtroEstado === 'completado' ? 'Completado' : filtroEstado === 'incompleto' ? 'Incompleto' : filtroEstado === 'pendiente' ? 'Pendiente' : 'seleccionado'}"`}
                  </p>
                </CardContent>
              </Card>
            );
          }

          return startupsFiltradas.map((startup) => {
          const estadoInfo = getEstadoBadge(startup.estado);
          const IconEstado = estadoInfo.icon;

          return (
            <Card key={startup.id} className="border-border/50 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn("p-2 rounded-lg", estadoInfo.bg)}>
                      <IconEstado className={cn("h-5 w-5", estadoInfo.color)} />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{startup.nombre}</CardTitle>
                      <CardDescription>
                        Fundador: {startup.fundador} • Arcángel: {startup.arcangel}
                      </CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={cn("px-2 py-1 rounded-md text-xs font-medium", estadoInfo.bg, estadoInfo.color)}>
                      {estadoInfo.label}
                    </span>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground">
                      <Mail className="h-4 w-4 inline mr-1" />
                      {startup.email}
                    </p>
                    {startup.ultimaActualizacion && (
                      <p className="text-xs text-muted-foreground">
                        Última actualización: {new Date(startup.ultimaActualizacion).toLocaleDateString('es-ES')}
                      </p>
                    )}
                    {/* Mostrar archivos adjuntos */}
                    {attachmentsByStartup[startup.id] && attachmentsByStartup[startup.id].length > 0 && (
                      <div className="mt-2 space-y-1">
                        <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                          <Paperclip className="h-3 w-3" />
                          Archivos adjuntos ({attachmentsByStartup[startup.id].length}):
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {attachmentsByStartup[startup.id].map((attachment) => (
                            <Button
                              key={attachment.id}
                              variant="outline"
                              size="sm"
                              className="h-7 text-xs"
                              onClick={() => downloadAttachment(attachment.id)}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              {attachment.filename}
                              {attachment.file_size && (
                                <span className="ml-1 text-muted-foreground">
                                  ({(attachment.file_size / 1024).toFixed(1)} KB)
                                </span>
                              )}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {startup.estado === 'pendiente' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => handleEnviarSolicitud(startup)}
                      >
                        <Mail className="h-4 w-4 mr-2" />
                        Enviar Solicitud
                      </Button>
                    )}
                    {startup.estado === 'incompleto' && (
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEnviarRecordatorio(startup)}
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          {startup.datos && (startup.datos.kpis || startup.datos.milestones?.length > 0) 
                            ? 'Recordatorio Personalizado' 
                            : 'Recordatorio'}
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleEnviarRecordatorioFinal(startup)}
                          className="text-orange-600 hover:text-orange-700"
                        >
                          <Mail className="h-4 w-4 mr-2" />
                          Recordatorio Final
                        </Button>
                      </div>
                    )}
                    {(startup.estado === 'completado' || startup.estado === 'completo') && (
                      <Button 
                        variant="default" 
                        size="sm"
                        onClick={() => handleVerDatos(startup)}
                      >
                        Ver Datos
                      </Button>
                    )}
                    {(startup.estado === 'pendiente' || startup.estado === 'incompleto') && (
                      <Button 
                        variant="default" 
                        size="sm"
                        onClick={() => handleVerDatos(startup)}
                      >
                        Ver Estado
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
          });
        })()}
      </div>
        </>
      )}
    </div>
  );
};

// Contenido por defecto (pre-escrito) basado en el informe de referencia (Pinama Capital 23 – Q3 2025). Se muestra cuando no hay contenido guardado para poder editarlo.
const CONTENIDO_REFERENCIA = {
  introduccion: `Nos es grato compartir el sexto informe de actividad de la sociedad y el estado de las participadas de Pinama Capital 23 correspondiente a los pasados meses de julio, agosto y septiembre. Distribuimos en esta fecha el informe por la dificultad que entraña conseguir que las startups nos trasladen información.

Durante el Q3 hemos materializado la inversión en 5 startups: Hair Up Laser, Orbital Paradigm, Invitadisima, Hulahoop y Robopedics, que se suman a las otras 10 que ya habíamos incorporado a la cartera desde el inicio de la actividad: Leemons, Zepo, Repscan, Cubro, Kibus, Sowify, Digital Anatomics, Distinkt, Phenyx Health y The Residential Club. Hemos aprobado también follow-on en Sowify y Digital Anatomics.

Mientras Leemons declaró concurso el pasado 4 de septiembre, Cubro ha supuesto el primer exit del vehículo en este trimestre, con una rentabilidad NETA (deducidos los gastos de la operación de compraventa):

- TIR: 73,25%
- MOIC: 1,91X

En línea con nuestro modelo colaborativo, estaremos encantados de escuchar, estudiar y, en su caso, incorporar todas las sugerencias de mejora que nos queráis trasladar relacionadas con el formato o el contenido de este documento.

El equipo coordinador de Pinama Capital 23`,
  resumen_ejecutivo: `En Q3 del 2025, la actividad del equipo coordinador se ha centrado fundamentalmente en:

• Hemos materializado nuestro primer exit: Cubro, en la que invertimos 100K € y recibimos 191K netos, con una TIR de 73,25%.
• Leemons presentó concurso de acreedores, lo que ha supuesto nuestra primera inversión fallida.
• +171 oportunidades de inversión analizadas y filtradas (54 recibidas en CRM y +117 descartadas previamente).

Inversiones formalizadas en el Q3 2025: ROBOPEDICS (75% votos — 75K €), HULAHOOP (69,6% — 60K €), INVITADISIMA (70K €), ORBITAL PARADIGM (88,2% — 75K €), HAIR UP LASER (76,5% — 75K €).

2 Comités celebrados: 1 Selección + 1 Inversión. Pese a la bajada del nivel de actividad en el ecosistema por el verano, hemos asistido a 7 eventos.

Nacho Alonso — Presidente`,
  resumen_cards: [
    { type: 'highlight', title: '', content: 'Hemos materializado nuestro primer exit: Cubro, en la que invertimos 100K € y recibimos 191K netos, con una TIR de 73,25%.', items: [] },
    { type: 'warning', title: '', content: 'Leemons presentó concurso de acreedores, lo que ha supuesto nuestra primera inversión fallida.', items: [] },
    { type: '', title: '', content: '+171 oportunidades de inversión analizadas y filtradas (54 recibidas en CRM y +117 descartadas previamente).', items: [] },
    { type: '', title: 'Inversiones aprobadas en el Q3 2025', content: '', items: ['INSPECT RAIL: 84,6% votos positivos — 100K €', '(follow-on) SOWIFY: 92,3% votos positivos — 30K €', '(follow-on) DIGITAL ANATOMICS: 84,7% votos positivos — 20K €'] },
    { type: '', title: 'Inversiones formalizadas en el Q3 2025', content: '', items: ['HAIR UP LASER: 76,5% votos — 75K €', 'ORBITAL PARADIGM: 88,2% votos — 75K €', 'INVITADISIMA: 50% votos + 25% abstención — 70K €', 'HULAHOOP: 69,6% votos — 60K €', 'ROBOPEDICS: 75% votos — 75K €'] },
    { type: '', title: '', content: '2 Comités celebrados: 1 Selección + 1 Inversión.', items: [] },
    { type: '', title: '', content: 'Pese a la bajada del nivel de actividad en el ecosistema por el verano, hemos asistido a 7 eventos.', items: [] }
  ],
  comites: [
    { nombre: '1 de Selección', fecha: '09 de septiembre', tipo: 'Selección', descripcion: 'Inspect Rail, Glotac, Banbu y Foodie Fame. (telemático)' },
    { nombre: '1 de Inversión', fecha: '22 de septiembre', tipo: 'Inversión', descripcion: 'Glotac e Inspect Rail. XVII Comité de Inversión celebrado en MIOTI Tech & Business School, Madrid.' }
  ],
  eventos: [
    { nombre: 'Presencia en eventos del ecosistema', fecha: 'Q3 2025', descripcion: 'Pese a la bajada del nivel de actividad en el ecosistema por el verano, hemos asistido a 7 eventos.', imagen: null },
    { nombre: 'Evento 2', fecha: 'Q3 2025', descripcion: 'Descripción del segundo evento.', imagen: null },
    { nombre: 'Evento 3', fecha: 'Q3 2025', descripcion: 'Descripción del tercer evento.', imagen: null },
    { nombre: 'Evento 4', fecha: 'Q3 2025', descripcion: 'Descripción del cuarto evento.', imagen: null }
  ],
  indicadores: {
    startups_vivas: 13,
    exits: 1,
    fallidas: 1,
    inicio_actividad: '9 oct 23',
    startups_recibidas_q3: '54/729',
    inversiones_q3: 5,
    n_socios: 35,
    reuniones_analisis_q3: 67,
    total_invertido_q3_eur: 355000,
    valor_teorico_cartera_eur: '4,295m',
    total_compromisos_desembolso_eur: 1148805,
    comites_seleccion_q3: 2,
    revalorizacion_cartera_eur: '2,14m',
    comites_inversion_q3: 1,
    revalorizacion_cartera_pct: '-0,74%',
    startups_aprobadas_q3: 3,
    startups_invertidas: 15
  },
  calendario: [
    { titulo: 'Consejo de administración', fecha: '6 de noviembre', descripcion: 'Consejo de administración: 6 de noviembre.' },
    { titulo: 'Comités de Inversión', fecha: '16 de diciembre', descripcion: 'Híbrido (IE Tower, Madrid).' },
    { titulo: 'Otros', fecha: 'Antes de final de año', descripcion: 'Se circulará con los socios el calendario de eventos de 2026.' }
  ],
  calendario_comites: {
    anio: 2025,
    seleccion: [
      { month: 1, day: 14 }, { month: 2, day: 11 }, { month: 3, day: 11 }, { month: 4, day: 8 }, { month: 5, day: 6 }, { month: 6, day: 3 },
      { month: 11, day: 4 }, { month: 12, day: 2 }, { month: 12, day: 8 }
    ],
    inversion: [
      { month: 1, day: 1 }, { month: 1, day: 28 }, { month: 2, day: 25 }, { month: 3, day: 25 }, { month: 4, day: 22 }, { month: 5, day: 20 },
      { month: 6, day: 17 }, { month: 8, day: 15 }, { month: 9, day: 22 }, { month: 10, day: 28 }, { month: 11, day: 18 }, { month: 12, day: 16 }, { month: 12, day: 25 }
    ]
  },
  anexo_i: `Tamaño del vehículo: €4,295m | Formato jurídico: Sociedad limitada
Duración: 10 años (4 inv. + 6 liq.). Inicio 9/10/2023
TIR objetivo: 24,2% (>3x) | Inversiones: 30-40 startups + Follow-ons
Tesis: Startups españolas innovadoras. Pre Seed/Seed. Multisector.
Inversión mínima socios: €50k (4 cap. calls) — Posibilidad de coinversión
Tamaño de las inversiones: 50k-150k €
Closings & capital calls (25% cada): 31-dic-23, Q4 24, Q4 25, Q4 2025
Compromiso GPs: 315k (7,33%) | Gastos fase inversión: 2,5% anual | Fase liquidación: 2% anual
Carried interest & hurdle: 18% — 8% capitalizado`,
  anexo_ii: `Anexo II: Calendario de Comités de Selección e Inversión 2025

Leyenda: ■ Comité Selección  ■ Comité Inversión`,
  anexo_iii_equipo: [
    { nombre: 'Nacho Alonso', rol: 'Cofundador – Presidente (GP)', descripcion: 'Relaciones institucionales y con inversores. Estrategia.', imagen: null },
    { nombre: 'Guillermo Soto', rol: 'Cofundador – Dtor General (GP)', descripcion: 'Relaciones con startups. Riesgos.', imagen: null },
    { nombre: 'Alejandro Soto', rol: 'Director Financiero', descripcion: 'Analista de inversiones.', imagen: null },
    { nombre: 'Andrea Alonso-Bernaola', rol: 'Dealflow & Portfolio Manager', descripcion: 'Analista de inversiones.', imagen: null },
    { nombre: 'Carlos Cameo', rol: 'Investment Associate', descripcion: 'Analista de inversiones.', imagen: null }
  ]
};

const INDICADORES_LABELS = {
  startups_vivas: 'Startups vivas',
  exits: 'Exits',
  fallidas: 'Fallidas',
  inicio_actividad: 'Inicio de actividad',
  startups_recibidas_q3: 'Nº startups recibidas Q3 / Total analizadas',
  inversiones_q3: 'Nº inversiones realizadas Q3',
  n_socios: 'Nº de socios inversores',
  reuniones_analisis_q3: 'Nº de reuniones análisis Q3',
  total_invertido_q3_eur: 'Total € invertidos Q3',
  valor_teorico_cartera_eur: 'Valor teórico cartera (m €)',
  total_compromisos_desembolso_eur: 'Total compromisos desembolso €',
  comites_seleccion_q3: 'Nº Comités de Selección Q3',
  revalorizacion_cartera_eur: 'Revalorización cartera (m €)',
  comites_inversion_q3: 'Nº Comités de Inversión Q3',
  revalorizacion_cartera_pct: 'Revalorización cartera %',
  startups_aprobadas_q3: 'Startups aprobadas Q3',
  startups_invertidas: 'Startups invertidas'
};

// Valor "vacío" para considerar que no hay contenido guardado (mostrar referencia).
function isValueEmpty(v) {
  if (v == null) return true;
  if (typeof v === 'string') return v.trim() === '';
  if (Array.isArray(v)) return v.length === 0;
  if (typeof v === 'object') return Object.keys(v).length === 0;
  return false;
}
function isEmptyContenido(c) {
  if (c == null || typeof c !== 'object') return true;
  const keys = Object.keys(c);
  if (keys.length === 0) return true;
  // Si todas las claves tienen valor vacío, tratar como sin contenido guardado
  return keys.every((k) => isValueEmpty(c[k]));
}
function mergeContenido(contenido) {
  if (isEmptyContenido(contenido)) return { ...CONTENIDO_REFERENCIA };
  const c = { ...contenido };
  if (c.equipo && !c.anexo_iii_equipo) c.anexo_iii_equipo = c.equipo;
  if (c.anexos != null && c.anexo_i == null) c.anexo_i = typeof c.anexos === 'string' ? c.anexos : '';
  if (!Array.isArray(c.resumen_cards)) c.resumen_cards = c.resumen_ejecutivo ? [] : (CONTENIDO_REFERENCIA.resumen_cards || []);
  if (!c.calendario_comites && CONTENIDO_REFERENCIA.calendario_comites) c.calendario_comites = CONTENIDO_REFERENCIA.calendario_comites;
  return { ...CONTENIDO_REFERENCIA, ...c };
}

function FormularioContenidoGenerico({ reporteId, contenido, onChange, onSave, guardando }) {
  const data = mergeContenido(contenido);
  const update = (key, value) => onChange({ ...data, [key]: value });

  const [rellenandoIA, setRellenandoIA] = useState(false);
  const [modalDriveOpen, setModalDriveOpen] = useState(false);
  const [driveFolderId, setDriveFolderId] = useState('root');
  const [driveFolderName, setDriveFolderName] = useState('Mi Drive');
  const [driveFolderHistory, setDriveFolderHistory] = useState([]);
  const [driveFiles, setDriveFiles] = useState([]);
  const [driveLoading, setDriveLoading] = useState(false);
  const [driveSelectedIds, setDriveSelectedIds] = useState(new Set());
  const [driveSearchQuery, setDriveSearchQuery] = useState('');
  const [driveSearchResults, setDriveSearchResults] = useState([]);
  const [driveSearching, setDriveSearching] = useState(false);
  const [driveSearchMode, setDriveSearchMode] = useState(false);
  const [openSections, setOpenSections] = useState({
    introduccion: true,
    resumen: false,
    resumenTarjetas: false,
    comites: true,
    eventos: false,
    indicadores: false,
    calendario: false,
    anexos: false,
  });
  const toggleSection = (key) => setOpenSections((s) => ({ ...s, [key]: !s[key] }));

  const DOC_MIMES = [
    'application/vnd.google-apps.document',
    'application/vnd.google-apps.spreadsheet',
    'application/vnd.google-apps.presentation', // Google Slides
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  ];
  const SHORTCUT_MIME = 'application/vnd.google-apps.shortcut';

  const isSelectableFile = (f) => {
    if (DOC_MIMES.includes(f.mimeType)) return true;
    if (f.mimeType === SHORTCUT_MIME && f.shortcutDetails) {
      return DOC_MIMES.includes(f.shortcutDetails.targetMimeType || '');
    }
    return false;
  };

  const loadDriveFiles = async (folderId) => {
    setDriveLoading(true);
    try {
      const res = await listDriveFiles(folderId);
      const list = res.files || [];
      const folders = list.filter(f => f.mimeType === 'application/vnd.google-apps.folder');
      const files = list.filter(f => f.mimeType !== 'application/vnd.google-apps.folder');
      setDriveFiles([...folders, ...files]);
    } catch (e) {
      console.error(e);
      setDriveFiles([]);
    } finally {
      setDriveLoading(false);
    }
  };

  const runDriveSearch = async () => {
    const q = driveSearchQuery.trim();
    if (!q) {
      setDriveSearchMode(false);
      loadDriveFiles(driveFolderId);
      return;
    }
    setDriveSearching(true);
    try {
      const res = await searchDriveFiles(q);
      const list = res.files || [];
      const folders = list.filter(f => f.mimeType === 'application/vnd.google-apps.folder');
      const files = list.filter(f => f.mimeType !== 'application/vnd.google-apps.folder');
      setDriveSearchResults([...folders, ...files]);
      setDriveSearchMode(true);
    } catch (e) {
      console.error(e);
      setDriveSearchResults([]);
    } finally {
      setDriveSearching(false);
    }
  };

  const openDriveModal = async () => {
    setModalDriveOpen(true);
    setDriveSelectedIds(new Set());
    setDriveSearchQuery('');
    setDriveSearchMode(false);
    setDriveSearchResults([]);
    try {
      const status = await getDriveStatus();
      if (!status.connected) {
        alert('Conecta Google Drive primero en Configuración.');
        setModalDriveOpen(false);
        return;
      }
      // Empezar en Mi Drive (root) para poder navegar por toda la unidad
      setDriveFolderId('root');
      setDriveFolderName('Mi Drive');
      setDriveFolderHistory([]);
      await loadDriveFiles('root');
    } catch (e) {
      console.error(e);
      setModalDriveOpen(false);
    }
  };

  const navigateDriveFolder = (folderId, folderName) => {
    setDriveSearchMode(false);
    setDriveFolderHistory(prev => [...prev, { id: driveFolderId, name: driveFolderName }]);
    setDriveFolderId(folderId);
    setDriveFolderName(folderName);
    loadDriveFiles(folderId);
  };

  const driveBack = () => {
    if (driveFolderHistory.length === 0) return;
    const prev = driveFolderHistory[driveFolderHistory.length - 1];
    setDriveFolderHistory(driveFolderHistory.slice(0, -1));
    setDriveFolderId(prev.id);
    setDriveFolderName(prev.name);
    loadDriveFiles(prev.id);
  };

  const toggleDriveSelection = (file) => {
    if (!isSelectableFile(file)) return;
    const id = file.id;
    setDriveSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runRellenarComites = async () => {
    if (driveSelectedIds.size === 0) {
      alert('Selecciona al menos un documento.');
      return;
    }
    setRellenandoIA(true);
    setModalDriveOpen(false);
    try {
      const res = await rellenarDesdeDrive(reporteId, { fileIds: [...driveSelectedIds] });
      const raw = res?.data;
      const newComites = Array.isArray(raw) ? raw : (raw?.comites ? raw.comites : []);
      if (newComites.length > 0) {
        update('comites', newComites.map(c => ({
          nombre: c.nombre ?? '',
          fecha: c.fecha ?? '',
          tipo: c.tipo ?? '',
          descripcion: c.descripcion ?? '',
        })));
      }
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error al rellenar Comités.');
    } finally {
      setRellenandoIA(false);
    }
  };

  const addComite = () => update('comites', [...(data.comites || []), { nombre: '', fecha: '', tipo: '', descripcion: '' }]);
  const setComite = (i, field, value) => {
    const comites = [...(data.comites || [])];
    comites[i] = { ...comites[i], [field]: value };
    update('comites', comites);
  };
  const removeComite = (i) => update('comites', data.comites.filter((_, j) => j !== i));

  const addEvento = () => update('eventos', [...(data.eventos || []), { nombre: '', fecha: '', descripcion: '', imagen: null }]);
  const setEvento = (i, field, value) => {
    const eventos = [...(data.eventos || [])];
    eventos[i] = { ...eventos[i], [field]: value };
    update('eventos', eventos);
  };
  const removeEvento = (i) => update('eventos', data.eventos.filter((_, j) => j !== i));

  const resumenCards = data.resumen_cards || [];
  const addResumenCard = () => update('resumen_cards', [...resumenCards, { type: '', title: '', content: '', items: [] }]);
  const setResumenCard = (i, field, value) => {
    const cards = [...resumenCards];
    cards[i] = { ...cards[i], [field]: value };
    update('resumen_cards', cards);
  };
  const setResumenCardItem = (cardIndex, itemIndex, value) => {
    const cards = [...resumenCards];
    const items = [...(cards[cardIndex].items || [])];
    items[itemIndex] = value;
    cards[cardIndex] = { ...cards[cardIndex], items };
    update('resumen_cards', cards);
  };
  const addResumenCardItem = (cardIndex) => {
    const cards = [...resumenCards];
    const items = [...(cards[cardIndex].items || []), ''];
    cards[cardIndex] = { ...cards[cardIndex], items };
    update('resumen_cards', cards);
  };
  const removeResumenCardItem = (cardIndex, itemIndex) => {
    const cards = [...resumenCards];
    const items = (cards[cardIndex].items || []).filter((_, j) => j !== itemIndex);
    cards[cardIndex] = { ...cards[cardIndex], items };
    update('resumen_cards', cards);
  };
  const removeResumenCard = (i) => update('resumen_cards', resumenCards.filter((_, j) => j !== i));

  const addCalendario = () => update('calendario', [...(data.calendario || []), { titulo: '', fecha: '', descripcion: '' }]);
  const setCalendario = (i, field, value) => {
    const cal = [...(data.calendario || [])];
    cal[i] = { ...cal[i], [field]: value };
    update('calendario', cal);
  };
  const removeCalendario = (i) => update('calendario', data.calendario.filter((_, j) => j !== i));

  const calComites = data.calendario_comites || { anio: new Date().getFullYear(), seleccion: [], inversion: [] };
  const setCalendarioComitesAnio = (v) => update('calendario_comites', { ...calComites, anio: parseInt(v, 10) || calComites.anio });
  const addCalendarioComitesSeleccion = () => update('calendario_comites', { ...calComites, seleccion: [...(calComites.seleccion || []), { month: 1, day: 1 }] });
  const setCalendarioComitesSeleccion = (i, field, value) => {
    const sel = [...(calComites.seleccion || [])];
    sel[i] = { ...sel[i], [field]: field === 'month' ? parseInt(value, 10) : parseInt(value, 10) || 1 };
    update('calendario_comites', { ...calComites, seleccion: sel });
  };
  const removeCalendarioComitesSeleccion = (i) => update('calendario_comites', { ...calComites, seleccion: (calComites.seleccion || []).filter((_, j) => j !== i) });
  const addCalendarioComitesInversion = () => update('calendario_comites', { ...calComites, inversion: [...(calComites.inversion || []), { month: 1, day: 1 }] });
  const setCalendarioComitesInversion = (i, field, value) => {
    const inv = [...(calComites.inversion || [])];
    inv[i] = { ...inv[i], [field]: field === 'month' ? parseInt(value, 10) : parseInt(value, 10) || 1 };
    update('calendario_comites', { ...calComites, inversion: inv });
  };
  const removeCalendarioComitesInversion = (i) => update('calendario_comites', { ...calComites, inversion: (calComites.inversion || []).filter((_, j) => j !== i) });

  const equipoList = data.anexo_iii_equipo ?? data.equipo ?? [];
  const addEquipo = () => update('anexo_iii_equipo', [...equipoList, { nombre: '', rol: '', descripcion: '', imagen: null }]);
  const setEquipo = (i, field, value) => {
    const list = [...equipoList];
    list[i] = { ...list[i], [field]: value };
    update('anexo_iii_equipo', list);
  };
  const removeEquipo = (i) => update('anexo_iii_equipo', equipoList.filter((_, j) => j !== i));
  const setIndicador = (key, value) => update('indicadores', { ...(data.indicadores || {}), [key]: value });

  const handleImageUpload = (file, callback) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => callback(reader.result);
    reader.readAsDataURL(file);
  };

  if (!reporteId) {
    return (
      <Card className="border-border/50 shadow-sm">
        <CardContent className="p-6 text-center text-muted-foreground">
          Guarda el reporte primero para editar la información genérica.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-border/50 shadow-sm">
        <CardHeader>
          <CardTitle>Información genérica del reporte</CardTitle>
          <CardDescription>Bloques por pasos (1–8). Haz clic en cada uno para abrirlo o cerrarlo.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex flex-wrap gap-1.5 pb-2 border-b border-border/50">
            {[
              { key: 'introduccion', label: '1. Intro' },
              { key: 'resumen', label: '2. Resumen' },
              { key: 'resumenTarjetas', label: '3. Tarjetas' },
              { key: 'comites', label: '4. Comités' },
              { key: 'eventos', label: '5. Eventos' },
              { key: 'indicadores', label: '6. Indicadores' },
              { key: 'calendario', label: '7. Calendario' },
              { key: 'anexos', label: '8. Anexos' },
            ].map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setOpenSections((s) => ({ ...s, [key]: !s[key] }))}
                className={cn(
                  'px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors',
                  openSections[key]
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {/* 1. Introducción */}
          <Collapsible open={openSections.introduccion} onOpenChange={() => toggleSection('introduccion')}>
            <CollapsibleTrigger className="w-full flex items-center gap-2 py-3 px-3 rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/50 text-left font-medium">
              {openSections.introduccion ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              <span>1. Introducción</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-3 pb-4 px-1 space-y-2">
                <label className="text-sm font-medium block mb-1">Introducción</label>
                <InputWithDictation
                  as="textarea"
                  className="w-full min-h-[280px] text-sm resize-y"
                  value={data.introduccion || ''}
                  onChange={(e) => update('introduccion', e.target.value)}
                  placeholder="Introducción del reporte..."
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* 2. Resumen ejecutivo (texto único) */}
          <Collapsible open={openSections.resumen} onOpenChange={() => toggleSection('resumen')}>
            <CollapsibleTrigger className="w-full flex items-center gap-2 py-3 px-3 rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/50 text-left font-medium">
              {openSections.resumen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              <span>2. Resumen ejecutivo (texto único)</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-3 pb-4 px-1">
                <InputWithDictation
                  as="textarea"
                  className="w-full min-h-[120px] text-sm resize-y"
                  value={data.resumen_ejecutivo || ''}
                  onChange={(e) => update('resumen_ejecutivo', e.target.value)}
                  placeholder="Si no usas tarjetas, se mostrará este texto en una sola tarjeta."
                />
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* 3. Resumen por tarjetas */}
          <Collapsible open={openSections.resumenTarjetas} onOpenChange={() => toggleSection('resumenTarjetas')}>
            <CollapsibleTrigger className="w-full flex items-center gap-2 py-3 px-3 rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/50 text-left font-medium">
              {openSections.resumenTarjetas ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              <span>3. Resumen ejecutivo por tarjetas</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-3 pb-4 px-1 space-y-2">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-muted-foreground">Si hay al menos una tarjeta, se usan en lugar del texto único.</p>
                  <Button type="button" variant="outline" size="sm" onClick={addResumenCard}>+ Añadir tarjeta</Button>
                </div>
                {(resumenCards || []).map((card, i) => (
              <div key={i} className="p-4 rounded-lg border border-border/50 mb-3 space-y-2">
                <div className="flex flex-wrap gap-2 items-center">
                  <select
                    className="rounded border border-input bg-background px-2 py-1 text-sm w-32"
                    value={card.type || ''}
                    onChange={(e) => setResumenCard(i, 'type', e.target.value)}
                  >
                    <option value="">Normal</option>
                    <option value="highlight">Destacado</option>
                    <option value="warning">Aviso</option>
                  </select>
                  <InputWithDictation className="flex-1 min-w-[200px] text-sm" placeholder="Título (opcional)" value={card.title || ''} onChange={(e) => setResumenCard(i, 'title', e.target.value)} />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeResumenCard(i)}>Quitar</Button>
                </div>
                <InputWithDictation as="textarea" className="w-full min-h-[60px] text-sm" placeholder="Contenido (párrafo)" value={card.content || ''} onChange={(e) => setResumenCard(i, 'content', e.target.value)} />
                <div>
                  <span className="text-xs text-muted-foreground">Lista de ítems (opcional):</span>
                  {(card.items || []).map((item, j) => (
                    <div key={j} className="flex gap-1 mt-1">
                      <InputWithDictation className="flex-1 text-sm" placeholder={`Ítem ${j + 1}`} value={item} onChange={(e) => setResumenCardItem(i, j, e.target.value)} />
                      <Button type="button" variant="ghost" size="sm" onClick={() => removeResumenCardItem(i, j)}>×</Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" className="mt-1" onClick={() => addResumenCardItem(i)}>+ Ítem</Button>
                </div>
              </div>
            ))}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* 4. Comités */}
          <Collapsible open={openSections.comites} onOpenChange={() => toggleSection('comites')}>
            <CollapsibleTrigger className="w-full flex items-center gap-2 py-3 px-3 rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/50 text-left font-medium">
              {openSections.comites ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              <span>4. Comités</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-3 pb-4 px-1 space-y-2">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <label className="text-sm font-medium">Comités</label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  disabled={rellenandoIA}
                  onClick={openDriveModal}
                >
                  {rellenandoIA ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FolderOpen className="h-4 w-4 mr-2" />}
                  Seleccionar documentos y rellenar Comités
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={addComite}>+ Añadir</Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mb-2">Abre Drive, elige los documentos de referencia y la IA rellenará fechas y startups manteniendo la estructura actual.</p>
            {(data.comites || []).map((c, i) => (
              <div key={i} className="flex flex-wrap gap-2 items-start p-2 rounded border border-border/50 mb-2">
                <InputWithDictation className="flex-1 min-w-[120px] text-sm" placeholder="Nombre" value={c.nombre || ''} onChange={(e) => setComite(i, 'nombre', e.target.value)} />
                <InputWithDictation className="w-32 text-sm" placeholder="Fecha" value={c.fecha || ''} onChange={(e) => setComite(i, 'fecha', e.target.value)} />
                <InputWithDictation className="w-28 text-sm" placeholder="Tipo" value={c.tipo || ''} onChange={(e) => setComite(i, 'tipo', e.target.value)} />
                <InputWithDictation className="flex-1 min-w-[160px] text-sm" placeholder="Descripción" value={c.descripcion || ''} onChange={(e) => setComite(i, 'descripcion', e.target.value)} />
                <Button type="button" variant="ghost" size="sm" onClick={() => removeComite(i)}>Quitar</Button>
              </div>
            ))}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* 5. Eventos */}
          <Collapsible open={openSections.eventos} onOpenChange={() => toggleSection('eventos')}>
            <CollapsibleTrigger className="w-full flex items-center gap-2 py-3 px-3 rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/50 text-left font-medium">
              {openSections.eventos ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              <span>5. Eventos (con foto opcional)</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-3 pb-4 px-1">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Eventos</label>
              <Button type="button" variant="outline" size="sm" onClick={addEvento}>+ Añadir</Button>
            </div>
            {(data.eventos || []).map((e, i) => (
              <div key={i} className="p-3 rounded border border-border/50 mb-2 space-y-2">
                <div className="flex flex-wrap gap-2 items-start">
                  <InputWithDictation className="flex-1 min-w-[120px] text-sm" placeholder="Nombre" value={e.nombre || ''} onChange={(ev) => setEvento(i, 'nombre', ev.target.value)} />
                  <InputWithDictation className="w-32 text-sm" placeholder="Fecha" value={e.fecha || ''} onChange={(ev) => setEvento(i, 'fecha', ev.target.value)} />
                  <InputWithDictation className="flex-1 min-w-[160px] text-sm" placeholder="Descripción" value={e.descripcion || ''} onChange={(ev) => setEvento(i, 'descripcion', ev.target.value)} />
                  <Button type="button" variant="ghost" size="sm" onClick={() => removeEvento(i)}>Quitar</Button>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs text-muted-foreground">Foto del evento:</span>
                  <label className={cn(
                    "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium border border-input bg-background h-9 px-3",
                    "hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors",
                    "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
                  )}>
                    <input
                      type="file"
                      accept="image/*"
                      className="sr-only"
                      onChange={(ev) => { const f = ev.target.files?.[0]; if (f) handleImageUpload(f, (dataUrl) => setEvento(i, 'imagen', dataUrl)); ev.target.value = ''; }}
                    />
                    <ImagePlus className="h-4 w-4" />
                    Subir foto
                  </label>
                  {e.imagen && (
                    <>
                      <img src={e.imagen} alt="" className="h-12 w-12 object-cover rounded border border-input" />
                      <Button type="button" variant="ghost" size="sm" onClick={() => setEvento(i, 'imagen', null)}>Quitar foto</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* 6. Indicadores */}
          <Collapsible open={openSections.indicadores} onOpenChange={() => toggleSection('indicadores')}>
            <CollapsibleTrigger className="w-full flex items-center gap-2 py-3 px-3 rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/50 text-left font-medium">
              {openSections.indicadores ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              <span>6. Indicadores</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-3 pb-4 px-1">
            <label className="text-sm font-medium block mb-2">Indicadores</label>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {Object.keys(INDICADORES_LABELS).map((key) => (
                <div key={key} className="flex flex-col gap-1">
                  <label className="text-xs text-muted-foreground">{INDICADORES_LABELS[key]}</label>
                  <InputWithDictation
                    type="text"
                    className="w-full text-sm"
                    value={data.indicadores && data.indicadores[key] !== undefined && data.indicadores[key] !== null ? String(data.indicadores[key]) : ''}
                    onChange={(e) => setIndicador(key, e.target.value)}
                  />
                </div>
              ))}
            </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* 7. Calendario */}
          <Collapsible open={openSections.calendario} onOpenChange={() => toggleSection('calendario')}>
            <CollapsibleTrigger className="w-full flex items-center gap-2 py-3 px-3 rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/50 text-left font-medium">
              {openSections.calendario ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              <span>7. Calendario</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-3 pb-4 px-1">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium">Calendario</label>
              <Button type="button" variant="outline" size="sm" onClick={addCalendario}>+ Añadir</Button>
            </div>
            {(data.calendario || []).map((c, i) => (
              <div key={i} className="flex flex-wrap gap-2 items-start p-2 rounded border border-border/50 mb-2">
                <InputWithDictation className="flex-1 min-w-[120px] text-sm" placeholder="Título" value={c.titulo || ''} onChange={(e) => setCalendario(i, 'titulo', e.target.value)} />
                <InputWithDictation className="w-32 text-sm" placeholder="Fecha" value={c.fecha || ''} onChange={(e) => setCalendario(i, 'fecha', e.target.value)} />
                <InputWithDictation className="flex-1 min-w-[160px] text-sm" placeholder="Descripción" value={c.descripcion || ''} onChange={(e) => setCalendario(i, 'descripcion', e.target.value)} />
                <Button type="button" variant="ghost" size="sm" onClick={() => removeCalendario(i)}>Quitar</Button>
              </div>
            ))}
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* 8. Anexos (I, II, III) */}
          <Collapsible open={openSections.anexos} onOpenChange={() => toggleSection('anexos')}>
            <CollapsibleTrigger className="w-full flex items-center gap-2 py-3 px-3 rounded-lg border border-border/50 bg-muted/30 hover:bg-muted/50 text-left font-medium">
              {openSections.anexos ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
              <span>8. Anexos (I, II, III)</span>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="pt-3 pb-4 px-1 space-y-4">
            <h3 className="text-sm font-semibold border-b pb-2">Anexos</h3>

            <div>
              <label className="text-sm font-medium block mb-1">Anexo I: Resumen del vehículo</label>
              <InputWithDictation
                as="textarea"
                className="w-full min-h-[280px] text-sm resize-y"
                value={data.anexo_i ?? ''}
                onChange={(e) => update('anexo_i', e.target.value)}
                placeholder="Tamaño del vehículo, formato jurídico, duración, TIR objetivo..."
              />
            </div>

            <div>
              <label className="text-sm font-medium block mb-2">Anexo II: Calendario de Comités de Selección e Inversión (grid)</label>
              <p className="text-xs text-muted-foreground mb-2">Año y fechas de Comité Selección (amarillo) e Inversión (rojo) para el calendario visual.</p>
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs">Año:</label>
                <input type="number" className="w-20 rounded border px-2 py-1 text-sm" value={calComites.anio || ''} onChange={(e) => setCalendarioComitesAnio(e.target.value)} min="2020" max="2030" />
              </div>
              <div className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">Comité Selección (días)</span>
                  <Button type="button" variant="outline" size="sm" onClick={addCalendarioComitesSeleccion}>+ Añadir</Button>
                </div>
                {(calComites.seleccion || []).map((sel, i) => (
                  <div key={i} className="flex gap-2 items-center mb-1">
                    <select className="w-28 rounded border px-2 py-1 text-sm" value={sel.month || 1} onChange={(e) => setCalendarioComitesSeleccion(i, 'month', e.target.value)}>
                      {['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'].map((label, m) => (
                        <option key={m} value={m + 1}>{label}</option>
                      ))}
                    </select>
                    <input type="number" className="w-14 rounded border px-2 py-1 text-sm" placeholder="Día" value={sel.day ?? ''} onChange={(e) => setCalendarioComitesSeleccion(i, 'day', e.target.value)} min={1} max={31} />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeCalendarioComitesSeleccion(i)}>×</Button>
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium">Comité Inversión (días)</span>
                  <Button type="button" variant="outline" size="sm" onClick={addCalendarioComitesInversion}>+ Añadir</Button>
                </div>
                {(calComites.inversion || []).map((inv, i) => (
                  <div key={i} className="flex gap-2 items-center mb-1">
                    <select className="w-28 rounded border px-2 py-1 text-sm" value={inv.month || 1} onChange={(e) => setCalendarioComitesInversion(i, 'month', e.target.value)}>
                      {['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'].map((label, m) => (
                        <option key={m} value={m + 1}>{label}</option>
                      ))}
                    </select>
                    <input type="number" className="w-14 rounded border px-2 py-1 text-sm" placeholder="Día" value={inv.day ?? ''} onChange={(e) => setCalendarioComitesInversion(i, 'day', e.target.value)} min={1} max={31} />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeCalendarioComitesInversion(i)}>×</Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">Anexo III: Equipo coordinador (con foto opcional)</label>
                <Button type="button" variant="outline" size="sm" onClick={addEquipo}>+ Añadir</Button>
              </div>
              {equipoList.map((m, i) => (
                <div key={i} className="p-3 rounded border border-border/50 mb-2 space-y-2">
                  <div className="flex flex-wrap gap-2 items-start">
                    <InputWithDictation className="flex-1 min-w-[120px] text-sm" placeholder="Nombre" value={m.nombre || ''} onChange={(ev) => setEquipo(i, 'nombre', ev.target.value)} />
                    <InputWithDictation className="flex-1 min-w-[120px] text-sm" placeholder="Rol" value={m.rol || ''} onChange={(ev) => setEquipo(i, 'rol', ev.target.value)} />
                    <InputWithDictation className="flex-1 min-w-[160px] text-sm" placeholder="Descripción" value={m.descripcion || ''} onChange={(ev) => setEquipo(i, 'descripcion', ev.target.value)} />
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeEquipo(i)}>Quitar</Button>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">Foto:</span>
                    <label className={cn(
                      "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium border border-input bg-background h-9 px-3",
                      "hover:bg-accent hover:text-accent-foreground cursor-pointer transition-colors",
                      "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2"
                    )}>
                      <input
                        type="file"
                        accept="image/*"
                        className="sr-only"
                        onChange={(ev) => { const f = ev.target.files?.[0]; if (f) handleImageUpload(f, (dataUrl) => setEquipo(i, 'imagen', dataUrl)); ev.target.value = ''; }}
                      />
                      <ImagePlus className="h-4 w-4" />
                      Subir foto
                    </label>
                    {m.imagen && (
                      <>
                        <img src={m.imagen} alt="" className="h-12 w-12 object-cover rounded border border-input" />
                        <Button type="button" variant="ghost" size="sm" onClick={() => setEquipo(i, 'imagen', null)}>Quitar foto</Button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <div className="pt-4">
          <Button onClick={() => onSave(data)} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Guardar información genérica'}
          </Button>
          </div>
        </CardContent>
      </Card>

      {/* Modal: seleccionar documentos de Drive para rellenar Comités */}
      {modalDriveOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setModalDriveOpen(false)}>
          <div className="bg-background border rounded-lg shadow-lg max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold">Seleccionar documentos de referencia (Drive)</h3>
              <Button type="button" variant="ghost" size="icon" onClick={() => setModalDriveOpen(false)}><X className="h-4 w-4" /></Button>
            </div>
            <p className="text-xs text-muted-foreground px-4 pb-1 border-b">Navega por toda tu unidad. Puedes elegir Documentos de Google, Hojas, Presentaciones (Slides/PPTX) y Word (.docx).</p>
            <div className="p-2 border-b flex items-center gap-2 flex-wrap">
              {driveFolderHistory.length > 0 && !driveSearchMode && (
                <Button type="button" variant="ghost" size="sm" onClick={driveBack}>← Atrás</Button>
              )}
              {driveSearchMode && (
                <Button type="button" variant="ghost" size="sm" onClick={() => { setDriveSearchMode(false); setDriveSearchQuery(''); loadDriveFiles(driveFolderId); }}>Ver carpeta actual</Button>
              )}
              <span className="text-sm text-muted-foreground truncate">{driveSearchMode ? `Búsqueda: "${driveSearchQuery}"` : driveFolderName}</span>
            </div>
            <div className="px-2 pb-2 flex gap-2 items-center border-b">
              <Search className="h-4 w-4 text-muted-foreground shrink-0" />
              <input
                type="text"
                placeholder="Buscar archivos o carpetas..."
                value={driveSearchQuery}
                onChange={(e) => setDriveSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && runDriveSearch()}
                className="flex-1 min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
              <Button type="button" variant="outline" size="sm" onClick={runDriveSearch} disabled={driveSearching}>
                {driveSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Buscar'}
              </Button>
            </div>
            <div className="overflow-auto flex-1 p-2 min-h-[200px]">
              {driveSearchMode ? (
                driveSearching ? (
                  <div className="flex items-center justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
                ) : (
                  <ul className="space-y-1">
                    {driveSearchResults.map(f => {
                      const isFolder = f.mimeType === 'application/vnd.google-apps.folder';
                      const selectable = isSelectableFile(f);
                      return (
                        <li key={f.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/50">
                          {isFolder ? (
                            <button type="button" className="flex items-center gap-2 flex-1 text-left" onClick={() => navigateDriveFolder(f.id, f.name)}>
                              <FolderOpen className="h-5 w-5 text-amber-600" />
                              <span className="truncate">{f.name}</span>
                            </button>
                          ) : selectable ? (
                            <button type="button" className="flex items-center gap-2 flex-1 text-left" onClick={() => toggleDriveSelection(f)}>
                              <input type="checkbox" checked={driveSelectedIds.has(f.id)} readOnly className="rounded" />
                              <FileIcon className="h-5 w-5 text-muted-foreground" />
                              <span className="truncate">{f.name}</span>
                            </button>
                          ) : (
                            <span className="flex items-center gap-2 flex-1 text-muted-foreground">
                              <FileIcon className="h-5 w-5" />
                              <span className="truncate">{f.name}</span>
                              <span className="text-xs">(no soportado)</span>
                            </span>
                          )}
                        </li>
                      );
                    })}
                    {driveSearchResults.length === 0 && (
                      <li className="text-sm text-muted-foreground py-4 text-center">Sin resultados</li>
                    )}
                  </ul>
                )
              ) : driveLoading ? (
                <div className="flex items-center justify-center py-8"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
              ) : (
                <ul className="space-y-1">
                  {driveFiles.map(f => {
                    const isFolder = f.mimeType === 'application/vnd.google-apps.folder';
                    const selectable = isSelectableFile(f);
                    return (
                      <li key={f.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted/50">
                        {isFolder ? (
                          <button type="button" className="flex items-center gap-2 flex-1 text-left" onClick={() => navigateDriveFolder(f.id, f.name)}>
                            <FolderOpen className="h-5 w-5 text-amber-600" />
                            <span className="truncate">{f.name}</span>
                          </button>
                        ) : (
                          <>
                            {selectable ? (
                              <button type="button" className="flex items-center gap-2 flex-1 text-left" onClick={() => toggleDriveSelection(f)}>
                                <input type="checkbox" checked={driveSelectedIds.has(f.id)} readOnly className="rounded" />
                                <FileIcon className="h-5 w-5 text-muted-foreground" />
                                <span className="truncate">{f.name}</span>
                              </button>
                            ) : (
                              <span className="flex items-center gap-2 flex-1 text-muted-foreground">
                                <FileIcon className="h-5 w-5" />
                                <span className="truncate">{f.name}</span>
                                <span className="text-xs">(no soportado)</span>
                              </span>
                            )}
                          </>
                        )}
                      </li>
                    );
                  })}
                  {!driveLoading && driveFiles.length === 0 && (
                    <li className="text-sm text-muted-foreground py-4 text-center">Carpeta vacía</li>
                  )}
                </ul>
              )}
            </div>
            <div className="p-4 border-t flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{driveSelectedIds.size} documento(s) seleccionado(s)</span>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setModalDriveOpen(false)}>Cancelar</Button>
                <Button type="button" disabled={driveSelectedIds.size === 0} onClick={runRellenarComites}>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Rellenar Comités con estos documentos
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export { INDICADORES_LABELS, CONTENIDO_REFERENCIA, mergeContenido };
export default PreparacionReportes;

